/**
 * The game rules — shared by the server (which works out the score) and the
 * phone (which draws it). One copy, so the two can never disagree.
 *
 * Nothing here is stored. XP is worked out from what he actually logged and
 * sold, every time, so the score is always the truth about the business.
 */

export const GRAMS_PER_OUNCE = 28.35

export const XP = {
  batch: 25,
  pack: 5,
  sale: 10,
  /** Plus one XP for every whole dollar of a sale. */
  perDollar: 1,
} as const

export const LEVELS = [
  { min: 0, title: 'Rookie' },
  { min: 200, title: 'Hustler' },
  { min: 600, title: 'Closer' },
  { min: 1500, title: 'Boss' },
  { min: 4000, title: 'Mogul' },
  { min: 10000, title: 'Legend' },
] as const

export function levelFor(xp: number): { index: number; title: string; floor: number; next: number | null; progress: number } {
  let index = 0
  for (let i = 0; i < LEVELS.length; i++) if (xp >= LEVELS[i]!.min) index = i
  const cur = LEVELS[index]!
  const nxt = LEVELS[index + 1]
  const progress = nxt ? (xp - cur.min) / (nxt.min - cur.min) : 1
  return { index, title: cur.title, floor: cur.min, next: nxt ? nxt.min : null, progress: Math.max(0, Math.min(1, progress)) }
}

export interface BadgeFacts {
  sales: number
  revenueCents: number
  bestDayCents: number
  bestStreak: number
  soldOutBatches: number
  batches: number
}

/** `icon` is a name from src/components/Icon.tsx. */
export const BADGES: { key: string; icon: string; title: string; hint: string; earned: (f: BadgeFacts) => boolean }[] = [
  { key: 'first_stash', icon: 'box', title: 'Stocked Up', hint: 'Log your first batch', earned: (f) => f.batches >= 1 },
  { key: 'first_sale', icon: 'cash', title: 'First Sale', hint: 'Sell your first package', earned: (f) => f.sales >= 1 },
  { key: 'sold_out', icon: 'broom', title: 'Sold Out', hint: 'Sell every package from a batch', earned: (f) => f.soldOutBatches >= 1 },
  { key: 'streak_3', icon: 'flame', title: 'Heating Up', hint: 'Sell 3 days in a row', earned: (f) => f.bestStreak >= 3 },
  { key: 'streak_7', icon: 'bolt', title: 'On Fire', hint: 'Sell 7 days in a row', earned: (f) => f.bestStreak >= 7 },
  { key: 'hundred_day', icon: 'hundred', title: 'Hundred Club', hint: '$100 in one day', earned: (f) => f.bestDayCents >= 10_000 },
  { key: 'fifty_sales', icon: 'rocket', title: 'Fifty Deep', hint: '50 sales', earned: (f) => f.sales >= 50 },
  { key: 'grand', icon: 'crown', title: 'The Grand', hint: '$1,000 sold', earned: (f) => f.revenueCents >= 100_000 },
]

/** "3.5 g", "1 oz", "2 each" — how an amount reads on screen. */
export function formatAmount(milli: number, unit: 'g' | 'each'): string {
  const n = milli / 1000
  if (unit === 'each') return `${trim(n)} each`
  if (n >= GRAMS_PER_OUNCE - 0.05 && Math.abs(n / GRAMS_PER_OUNCE - Math.round(n / GRAMS_PER_OUNCE * 4) / 4) < 0.01) {
    return `${trim(Math.round(n / GRAMS_PER_OUNCE * 4) / 4)} oz`
  }
  return `${trim(n)} g`
}

function trim(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/0+$/, '').replace(/\.$/, '')
}

export function money(cents: number): string {
  const sign = cents < 0 ? '-' : ''
  const abs = Math.abs(cents)
  return `${sign}$${(abs / 100).toLocaleString('en-US', { minimumFractionDigits: abs % 100 ? 2 : 0, maximumFractionDigits: 2 })}`
}
